import {
  flowScheduleErrorMessage,
  useFlowSchedule,
  useUpdateFlowSchedule,
} from "@/api/flowSchedules";
import { PaneBackButton } from "@/components/PaneBackButton";
import { usePaneRouterContextSafe } from "@/components/PaneRouter";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ROUTES } from "@/constants/routes";
import { useAppNavigate, usePaneParams } from "@/hooks/pane-routing";
import {
  ObjectActionPaneHeaderKebabMenu,
  useAutomationObjectActionDeps,
  useObjectActions,
  type ObjectActionHandlers,
  type ObjectKind,
} from "@/object-actions";
import { AutomationCreateForm } from "@/panes/automation/AutomationCreateForm";
import { AutomationInstructionsEditor } from "@/panes/automation/AutomationInstructionsEditor";
import { AutomationLocationRow } from "@/panes/automation/AutomationLocationRow";
import { AutomationRunsList } from "@/panes/automation/AutomationRunsList";
import { AutomationScheduleEditor } from "@/panes/automation/AutomationScheduleEditor";
import {
  describeCadence,
  type AutomationState,
} from "@/panes/automation/cadence";
import {
  browserTimeZone,
  draftFromSchedule,
  draftToCron,
  savedCronForCompare,
  usesStructuredEditor,
  type ScheduleDraft,
} from "@/panes/automation/scheduleDraft";
import { useAutomationLocation } from "@/panes/automation/useAutomationLocation";
import {
  CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS,
  CANVAS_DETAIL_PANE_HEADER_CLASS,
} from "@/panes/canvasDetailPaneHeaderClasses";
import { cn } from "@/utils";
import type {
  FlowSchedule,
  UpdateFlowScheduleRequest,
} from "@cohesive/api-client";
import {
  PaneLayout,
  PaneLayoutContent,
  PaneLayoutHeader,
  PaneLayoutHeaderActions,
  Spinner,
} from "@cohesive/ui-components";
import { ChevronRight, X, Zap } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";

const AUTOMATION_KIND: ObjectKind = { type: "automation" };

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <h3 className="text-xs font-medium text-n-50">{children}</h3>;
}

export const AutomationPane = () => {
  const params = usePaneParams(ROUTES.AUTOMATIONS.SHOW);
  const automationId = params.id;
  const appNavigate = useAppNavigate();
  const paneContext = usePaneRouterContextSafe();

  const mode = automationId === "new" ? "create" : "edit";
  const isCreateMode = mode === "create";

  const {
    data: schedule,
    isLoading,
    isError,
  } = useFlowSchedule(automationId || "", {
    enabled: !isCreateMode && !!automationId,
  });

  const canEdit = !isCreateMode && !!schedule && schedule.can_edit;
  // The settings disclosure is pane-level state so the status pill can open it
  // when a paused automation has no cadence to resume.
  const [settingsOpen, setSettingsOpen] = useState(false);
  const scheduleSectionRef = useRef<HTMLDivElement | null>(null);

  // Pill state with the SkillPane optimistic-ref pattern: reflect the click
  // immediately, reconcile when the PATCH lands (or roll back on error). The
  // pane outlives a selection change, so the pending value carries the id it
  // belongs to — otherwise switching automations mid-toggle would paint the
  // next one's pill with the previous one's state.
  const optimisticState = useRef<{ id: string; state: AutomationState } | null>(
    null,
  );
  const { mutate: updateSchedule, isPending: isTogglingState } =
    useUpdateFlowSchedule({
      onError: (error) => {
        optimisticState.current = null;
        toast.error(
          flowScheduleErrorMessage(error, "Failed to update automation."),
        );
      },
    });

  const serverState: AutomationState | undefined = schedule
    ? describeCadence(schedule).state
    : undefined;
  const pending = optimisticState.current;
  if (
    pending &&
    (pending.id !== automationId || serverState === pending.state)
  ) {
    optimisticState.current = null;
  }
  const effectiveState = optimisticState.current?.state ?? serverState;
  const isActive = effectiveState === "active";

  const handleTogglePill = () => {
    if (!schedule || !canEdit) return;
    if (isActive) {
      optimisticState.current = { id: schedule.id, state: "paused" };
      updateSchedule({ id: schedule.id, data: { next_run_at: null } });
      return;
    }
    if (schedule.cron) {
      optimisticState.current = { id: schedule.id, state: "active" };
      updateSchedule({ id: schedule.id, data: { cron: schedule.cron } });
      return;
    }
    // No retained cadence to resume — open the settings and hand off to the
    // schedule controls (waiting a beat for the disclosure to expand).
    setSettingsOpen(true);
    window.setTimeout(() => {
      scheduleSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 150);
  };

  const deps = useAutomationObjectActionDeps(schedule, { isPaneActive: true });

  const [kebabOpen, setKebabOpen] = useState(false);
  // The kebab carries only Run now / Duplicate / Open location / Delete —
  // pause/resume belongs to the pill and edit to the pane itself, so those
  // handlers are omitted and the resolver drops them here.
  const kebabHandlers = useMemo((): ObjectActionHandlers => {
    if (isCreateMode || !schedule) return {};
    return {
      "run-now": () => window.setTimeout(deps.runNow, 0),
      duplicate: () => window.setTimeout(deps.duplicate, 0),
      "open-location": () => window.setTimeout(deps.openLocation, 0),
      delete: () => window.setTimeout(deps.deleteAutomation, 0),
    };
  }, [deps, isCreateMode, schedule]);

  const { actions: kebabActions } = useObjectActions({
    kind: AUTOMATION_KIND,
    surface: "left-rail",
    enabled: kebabOpen && !isCreateMode,
    state: { isAutomationActive: isActive },
    handlers: kebabHandlers,
  });

  return (
    <PaneLayout>
      <PaneLayoutHeader className={CANVAS_DETAIL_PANE_HEADER_CLASS}>
        <PaneLayoutHeaderActions
          className={CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS}
        >
          <PaneBackButton />
          {isCreateMode ? (
            <span className="truncate pl-1 text-sm font-medium">
              New automation
            </span>
          ) : (
            schedule && (
              <div className="flex min-w-0 items-center gap-2 pl-1">
                <div
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border text-n-50"
                  aria-hidden
                >
                  <Zap className="h-4 w-4" />
                </div>
                <span className="truncate text-sm font-medium">
                  {schedule.name}
                </span>
              </div>
            )
          )}
          <div className="flex-1" />
          <TooltipProvider>
            {!isCreateMode && schedule && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={handleTogglePill}
                    disabled={isTogglingState || !canEdit}
                    className="flex h-7 items-center gap-1.5 rounded-md border px-2 text-xs font-medium transition-colors hover:bg-n-05 disabled:opacity-60"
                  >
                    <span
                      className={cn(
                        "size-1.5 rounded-full",
                        isActive ? "bg-lc-active" : "bg-n-40",
                      )}
                      aria-hidden
                    />
                    {isActive ? "Active" : "Paused"}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  {isActive
                    ? "Active — runs on its schedule. Click to pause."
                    : schedule.cron
                      ? "Paused — keeps its schedule. Click to resume."
                      : "No schedule yet — pick a cadence to activate."}
                </TooltipContent>
              </Tooltip>
            )}
            {!isCreateMode && (
              // Registry-driven kebab (surface `left-rail`); non-modal so the
              // delete dialog can stack without Radix freezing <body>.
              <ObjectActionPaneHeaderKebabMenu
                actions={kebabActions}
                kind={AUTOMATION_KIND}
                name={schedule?.name}
                open={kebabOpen}
                onOpenChange={setKebabOpen}
              />
            )}
            {paneContext && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="toolbarGhost"
                    size="icon-sm"
                    className="shrink-0"
                    onClick={() => appNavigate({ pane: "rightPane", to: null })}
                    aria-label="Close"
                  >
                    <X />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Close</TooltipContent>
              </Tooltip>
            )}
          </TooltipProvider>
        </PaneLayoutHeaderActions>
      </PaneLayoutHeader>
      <PaneLayoutContent>
        {isCreateMode ? (
          <div className="h-full overflow-y-auto p-4">
            <AutomationCreateForm />
          </div>
        ) : isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Spinner size="lg" />
          </div>
        ) : isError || !schedule ? (
          // Distinguish a failed/removed automation from loading so the pane
          // never spins forever (e.g. deleted in another tab).
          <div className="flex h-full flex-col items-center justify-center gap-1 text-center">
            <p className="text-sm font-medium text-foreground/80">
              This automation couldn&rsquo;t be loaded
            </p>
            <p className="text-sm text-muted-foreground">
              It may have been deleted or you no longer have access.
            </p>
          </div>
        ) : (
          <AutomationEditor
            // Keyed per automation so the draft is seeded once and refetches
            // (pause/resume, run-now) never clobber in-flight edits.
            key={schedule.id}
            schedule={schedule}
            canEdit={canEdit}
            settingsOpen={settingsOpen}
            onSettingsOpenChange={setSettingsOpen}
            scheduleSectionRef={scheduleSectionRef}
          />
        )}
      </PaneLayoutContent>
    </PaneLayout>
  );
};

/** The server-side values the draft is compared against to derive dirtiness. */
interface SavedSnapshot {
  name: string;
  instructions: string;
  cron: string;
  timeZone: string;
}

function snapshotOf(schedule: FlowSchedule): SavedSnapshot {
  return {
    name: schedule.name,
    instructions: schedule.instructions ?? "",
    // Normalized through the editor's own round-trip, or an untouched schedule
    // whose cron the server wrote in another shape (`0 9 * * 1-5`) would read
    // as dirty and a Save would PATCH the cadence it never edited.
    cron: savedCronForCompare(schedule),
    // Match draftFromSchedule's fallback, or an untouched schedule with no
    // stored zone would read as dirty the moment the pane opens.
    timeZone: schedule.cron_time_zone || browserTimeZone(),
  };
}

/**
 * The editable body: settings behind a disclosure at the top, the instructions
 * document filling everything below it, and one Save for the lot. Edits are
 * local until Save — only the header's pause/resume acts immediately, since
 * that's an object-level state change rather than a field edit.
 */
function AutomationEditor({
  schedule,
  canEdit,
  settingsOpen,
  onSettingsOpenChange,
  scheduleSectionRef,
}: {
  schedule: FlowSchedule;
  canEdit: boolean;
  settingsOpen: boolean;
  onSettingsOpenChange: (open: boolean) => void;
  scheduleSectionRef: React.RefObject<HTMLDivElement | null>;
}) {
  const [saved, setSaved] = useState<SavedSnapshot>(() => snapshotOf(schedule));
  const [name, setName] = useState(saved.name);
  const [instructions, setInstructions] = useState(saved.instructions);
  const [scheduleDraft, setScheduleDraft] = useState<ScheduleDraft | null>(
    () => (usesStructuredEditor(schedule) ? draftFromSchedule(schedule) : null),
  );
  // Bumped on discard to re-seed the (otherwise seed-once) markdown editor.
  const [resetKey, setResetKey] = useState(0);

  const location = useAutomationLocation(schedule);
  const cadence = describeCadence(schedule);

  const { mutate: updateSchedule, isPending: isSaving } = useUpdateFlowSchedule(
    {
      onError: (error) =>
        toast.error(
          flowScheduleErrorMessage(error, "Failed to save automation."),
        ),
    },
  );

  const draftCron = scheduleDraft ? (draftToCron(scheduleDraft) ?? "") : null;
  const scheduleDirty =
    draftCron !== null &&
    (draftCron !== saved.cron ||
      (!!draftCron && scheduleDraft!.timeZone !== saved.timeZone));

  const trimmedName = name.trim();
  const isDirty =
    trimmedName !== saved.name ||
    instructions.trim() !== saved.instructions.trim() ||
    scheduleDirty;
  // The server rejects both empty, so block the save rather than surfacing a 400.
  const isValid = !!trimmedName && !!instructions.trim();

  const discard = () => {
    setName(saved.name);
    setInstructions(saved.instructions);
    setScheduleDraft(
      usesStructuredEditor(schedule) ? draftFromSchedule(schedule) : null,
    );
    setResetKey((key) => key + 1);
  };

  const save = () => {
    if (!isDirty || !isValid) return;
    const data: UpdateFlowScheduleRequest = {};
    if (trimmedName !== saved.name) data.name = trimmedName;
    if (instructions.trim() !== saved.instructions.trim()) {
      data.instructions = instructions;
    }
    if (scheduleDirty && scheduleDraft) {
      const cron = draftToCron(scheduleDraft);
      if (cron === null) {
        // Fully off — the server clears the cron-derived next run too.
        data.cron = null;
      } else {
        data.cron = cron;
        data.cron_time_zone = scheduleDraft.timeZone;
      }
    }

    updateSchedule(
      { id: schedule.id, data },
      {
        onSuccess: (updated) => {
          // Quiet state swap: the footer settles back to clean, no toast.
          setSaved(snapshotOf(updated));
          setName(updated.name);
        },
      },
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Settings — collapsed by default so the prompt owns the pane. The
          trigger carries a summary, so the cadence and location stay legible
          without expanding. */}
      <Collapsible
        open={settingsOpen}
        onOpenChange={onSettingsOpenChange}
        className="shrink-0 border-b"
      >
        <CollapsibleTrigger className="flex w-full items-center gap-2 px-4 py-2.5 text-left transition-colors hover:bg-n-05">
          <ChevronRight
            className={cn(
              "size-3.5 shrink-0 text-n-50 transition-transform duration-150",
              settingsOpen && "rotate-90",
            )}
            aria-hidden
          />
          <span className="shrink-0 text-xs font-medium text-n-70">
            Settings
          </span>
          {!settingsOpen && (
            <span className="min-w-0 truncate text-xs text-n-40">
              {cadence.short} · {location.label}
            </span>
          )}
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="flex flex-col gap-5 px-4 pb-4">
            <div className="flex flex-col gap-1.5">
              <SectionLabel>Name</SectionLabel>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!canEdit}
                aria-label="Automation name"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <SectionLabel>Schedule</SectionLabel>
              <AutomationScheduleEditor
                ref={scheduleSectionRef}
                schedule={schedule}
                value={scheduleDraft}
                onChange={setScheduleDraft}
                disabled={!canEdit}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <SectionLabel>Location</SectionLabel>
              <AutomationLocationRow schedule={schedule} />
            </div>

            <div className="flex flex-col gap-1.5">
              <SectionLabel>Recent runs</SectionLabel>
              <AutomationRunsList flowScheduleId={schedule.id} />
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Instructions — the working surface, given every pixel the settings
          disclosure isn't using. */}
      <div className="flex min-h-0 flex-1 flex-col gap-1.5 px-4 pb-4 pt-3">
        <SectionLabel>Instructions</SectionLabel>
        <AutomationInstructionsEditor
          namespaceKey={`${schedule.id}-${resetKey}`}
          value={saved.instructions}
          onChange={setInstructions}
          readOnly={!canEdit}
        />
      </div>

      {canEdit && (
        <div className="flex h-12 shrink-0 items-center justify-end gap-2 border-t px-4">
          {isDirty && (
            <span className="mr-auto truncate text-xs text-n-50">
              {isValid
                ? "Unsaved changes"
                : "Name and instructions are required"}
            </span>
          )}
          {isDirty && (
            <Button
              variant="ghost"
              size="sm"
              onClick={discard}
              disabled={isSaving}
            >
              Discard
            </Button>
          )}
          <Button
            size="sm"
            onClick={save}
            disabled={!isDirty || !isValid || isSaving}
            isLoading={isSaving}
          >
            Save
          </Button>
        </div>
      )}
    </div>
  );
}
